import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { GlassCard } from '../components/GlassCard';
import GlassTable from '../components/GlassTable';
import { BookOpen, Check, AlertTriangle } from 'lucide-react';

const PERIODS = ['30d', '90d', 'ytd'];
const TABS = [
  { key: 'tb', label: 'Trial Balance', testId: 'erp-tab-tb' },
  { key: 'pnl', label: 'P&L', testId: 'erp-tab-pnl' },
  { key: 'bs', label: 'Balance Sheet', testId: 'erp-tab-bs' },
];

export default function ERP() {
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney, taxLabel } = useTenantFormat();
  const fmtINR = formatMoney;
  const [tab, setTab] = useState('tb');
  const [period, setPeriod] = useState('30d');
  const [tbData, setTbData] = useState(null);
  const [pnlData, setPnlData] = useState(null);
  const [bsData, setBsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const load = async () => {
      try {
        if (tab === 'tb') { setTbData(await api.erpTrialBalance(token, period)); }
        else if (tab === 'pnl') { setPnlData(await api.erpPnL(token, period)); }
        else { setBsData(await api.erpBalanceSheet(token)); }
      } catch (e) {
        if (e.status === 401) return;
        if (e.status === 403) return;
        toast.show(e.message || 'Failed to load', 'error');
      } finally { setLoading(false); }
    };
    load();
  }, [token, tab, period, toast]);

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="erp-page">
      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-2xl bg-white/5 border border-white/10 w-fit">
        {TABS.map(t => (
          <button
            key={t.key}
            data-testid={t.testId}
            onClick={() => setTab(t.key)}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              tab === t.key ? 'bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/25' : 'text-white/50 hover:text-white/80'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Period selector for TB/PnL */}
      {tab !== 'bs' && (
        <div className="flex gap-1.5">
          {PERIODS.map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                period === p ? 'bg-white/10 text-white border border-white/20' : 'text-white/40 hover:text-white/60'
              }`}
            >
              {p.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="py-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && tab === 'tb' && tbData && <TrialBalanceView data={tbData} fmtINR={fmtINR} />}
      {!loading && tab === 'pnl' && pnlData && <PnLView data={pnlData} fmtINR={fmtINR} />}
      {!loading && tab === 'bs' && bsData && <BalanceSheetView data={bsData} fmtINR={fmtINR} taxLabel={taxLabel} />}
    </div>
  );
}

/* ── Trial Balance ── */
function TrialBalanceView({ data, fmtINR }) {
  const rows = data.rows || [];
  const groups = {};
  rows.forEach(r => {
    const t = r.account_type || 'other';
    if (!groups[t]) groups[t] = [];
    groups[t].push(r);
  });
  const totals = data.totals || {};

  return (
    <div className="space-y-4">
      <GlassCard dark className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-white/50 text-xs font-bold uppercase tracking-wider px-5 py-3">Account</th>
                <th className="text-left text-white/50 text-xs font-bold uppercase tracking-wider px-5 py-3">Type</th>
                <th className="text-right text-white/50 text-xs font-bold uppercase tracking-wider px-5 py-3">Debit ₹</th>
                <th className="text-right text-white/50 text-xs font-bold uppercase tracking-wider px-5 py-3">Credit ₹</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groups).map(([type, accts]) => (
                accts.map((r, i) => (
                  <tr key={`${type}-${i}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-white font-semibold">{r.account_name}</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-0.5 rounded bg-white/8 text-white/50 text-[10px] font-bold uppercase">{type}</span>
                    </td>
                    <td className="px-5 py-3 text-right text-white tabular-nums">{r.debit_rupees > 0 ? fmtINR(r.debit_rupees) : '—'}</td>
                    <td className="px-5 py-3 text-right text-white tabular-nums">{r.credit_rupees > 0 ? fmtINR(r.credit_rupees) : '—'}</td>
                  </tr>
                ))
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-white/15 bg-white/[0.03]">
                <td colSpan={2} className="px-5 py-3 text-white font-extrabold text-sm">TOTALS</td>
                <td className="px-5 py-3 text-right text-white font-extrabold tabular-nums">{fmtINR(totals.debit_rupees)}</td>
                <td className="px-5 py-3 text-right text-white font-extrabold tabular-nums">{fmtINR(totals.credit_rupees)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </GlassCard>
      <div className="flex items-center gap-2">
        {totals.balances_ok ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold border border-emerald-500/25">
            <Check size={14} /> Books balance
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/15 text-red-400 text-xs font-bold border border-red-500/25">
            <AlertTriangle size={14} /> Books do not balance
          </span>
        )}
      </div>
    </div>
  );
}

/* ── P&L ── */
function PnLView({ data, fmtINR }) {
  const kpis = [
    { label: 'Revenue', value: data.revenue, color: '#10B981' },
    { label: 'COGS', value: data.cogs, color: '#F59E0B' },
    { label: 'Gross Profit', value: data.gross_profit, color: '#14B8A6' },
    { label: 'Operating Expenses', value: data.operating_expenses_total, color: '#EF4444' },
    { label: 'Net Profit', value: data.net_profit, color: data.net_profit >= 0 ? '#10B981' : '#EF4444' },
  ];
  const marginPct = data.revenue > 0 ? ((data.net_profit / data.revenue) * 100).toFixed(1) : '0.0';
  const expenses = data.operating_expenses || [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {kpis.map(k => (
          <GlassCard dark key={k.label} className="p-4">
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">{k.label}</p>
            <p className="text-white font-extrabold text-lg tabular-nums mt-1" style={{ color: k.color }}>{fmtINR(k.value)}</p>
          </GlassCard>
        ))}
      </div>
      <div className="flex gap-2">
        <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 text-xs font-bold tabular-nums">
          Net Margin: {marginPct}%
        </span>
        <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 text-xs font-bold tabular-nums">
          Gross Margin: {data.revenue > 0 ? ((data.gross_profit / data.revenue) * 100).toFixed(1) : '0.0'}%
        </span>
      </div>
      {expenses.length > 0 && (
        <GlassCard dark className="overflow-hidden">
          <div className="px-5 py-3 border-b border-white/10">
            <p className="text-white/60 text-xs font-bold uppercase tracking-wider">Operating Expenses by Category</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {expenses.map((e, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-white font-semibold">{e.category}</td>
                    <td className="px-5 py-3 text-right text-white tabular-nums font-bold">{fmtINR(e.amount)}</td>
                    <td className="px-5 py-3 text-right text-white/40 tabular-nums text-xs">
                      {data.operating_expenses_total > 0 ? ((e.amount / data.operating_expenses_total) * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}
    </div>
  );
}

/* ── Balance Sheet ── */
function BalanceSheetView({ data, fmtINR, taxLabel }) {
  const a = data.assets || {};
  const l = data.liabilities || {};
  const e = data.equity || {};

  return (
    <div className="space-y-5">
      <p className="text-white/40 text-xs">As of {data.as_of}</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Assets */}
        <GlassCard dark className="p-5">
          <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-4">Assets</h3>
          <div className="space-y-3">
            <BsRow label="Cash / Bank" value={a.cash} fmtINR={fmtINR} />
            <BsRow label="Accounts Receivable" value={a.accounts_receivable} fmtINR={fmtINR} />
            <BsRow label="Inventory" value={a.inventory} fmtINR={fmtINR} />
            <div className="border-t border-white/10 pt-3">
              <BsRow label="Total Assets" value={a.total} bold fmtINR={fmtINR} />
            </div>
          </div>
        </GlassCard>
        {/* Liabilities + Equity */}
        <GlassCard dark className="p-5">
          <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-4">Liabilities</h3>
          <div className="space-y-3">
            <BsRow label="Accounts Payable" value={l.accounts_payable} fmtINR={fmtINR} />
            <BsRow label={`${taxLabel} Payable`} value={l.gst_payable} fmtINR={fmtINR} />
            <div className="border-t border-white/10 pt-3">
              <BsRow label="Total Liabilities" value={l.total} bold fmtINR={fmtINR} />
            </div>
          </div>
          <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mt-5 mb-4">Equity</h3>
          <div className="space-y-3">
            <BsRow label="Retained Earnings" value={e.retained_earnings} fmtINR={fmtINR} />
            <div className="border-t border-white/10 pt-3">
              <BsRow label="Total Equity" value={e.total} bold fmtINR={fmtINR} />
            </div>
          </div>
        </GlassCard>
      </div>
      <div className="flex items-center gap-2">
        {data.balances_ok ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold border border-emerald-500/25">
            <Check size={14} /> Balanced
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/15 text-red-400 text-xs font-bold border border-red-500/25">
            <AlertTriangle size={14} /> Not balanced
          </span>
        )}
      </div>
    </div>
  );
}

function BsRow({ label, value, bold, fmtINR: fmt }) {
  const fallback = (n) => { if (n == null) return '₹0'; try { return '₹' + Math.round(n).toLocaleString('en-IN'); } catch { return '₹' + Math.round(n); } };
  const f = fmt || fallback;
  return (
    <div className="flex justify-between items-center">
      <span className={`text-sm ${bold ? 'text-white font-extrabold' : 'text-white/70'}`}>{label}</span>
      <span className={`tabular-nums text-sm ${bold ? 'text-white font-extrabold' : 'text-white/90'}`}>{f(value)}</span>
    </div>
  );
}


